import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Video, ChevronLeft, ChevronRight, Globe, Check } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
import { trackDemoBooked } from '../lib/analytics';
export function BookDemo() {
  useScreenInit();
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isBooked, setIsBooked] = useState(false);
  const timeSlots = ['9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '1:00 PM', '1:30 PM', '2:00 PM', '3:00 PM'];
  const handleBook = () => {
    trackDemoBooked(selectedDate ?? undefined, selectedTime ?? undefined);
    setIsBooked(true);
  };
  return <div className="min-h-screen bg-[#1C1510] flex items-center justify-center py-24 px-6 relative overflow-hidden" data-id="element-6157" data-mp="2mruioh6h3xm0">
      <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-6158" data-mp="pv3pxxievmb3" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full bg-[#F97316]/[0.08] blur-[120px]" data-id="element-6159" data-mp="kwah4ie2kjh9" />

      <div className="relative z-10 w-full max-w-5xl" data-id="element-6160" data-mp="2ae5qrftw4uhy">
        {!isBooked ? <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-[#2B211A] border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row" data-id="element-6161" data-mp="1whsld52msjju">
            {/* Left info panel */}
            <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-white/5 md:w-1/3 bg-black/20" data-id="element-6162" data-mp="2u6c7ir0s6k8q">
              <div className="w-12 h-12 bg-[#EA580C] rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-[#EA580C]/20" data-id="element-6163" data-mp="2keg4d1ju83xn">
                <Calendar className="w-6 h-6 text-white" data-id="element-6164" data-mp="382ze7lijcoru" />
              </div>
              <h1 className="font-title font-bold text-2xl text-white mb-4" data-id="element-6165" data-mp="3cuh6ll6hth17">
                Giga Live Demo
              </h1>
              <div className="space-y-4 text-neutral-400" data-id="element-6166" data-mp="3mpoc5ix52tw4">
                <div className="flex items-center gap-3" data-id="element-6167" data-mp="3hrmqcq8bwggy">
                  <Clock className="w-5 h-5" data-id="element-6168" data-mp="3hs637sj4geo4" />
                  <span data-id="element-6169" data-mp="3cs9w3kf4o74c">30 min</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-6170" data-mp="3cqqg7uxdvy33">
                  <Video className="w-5 h-5" data-id="element-6171" data-mp="38rpx0bazutfk" />
                  <span data-id="element-6172" data-mp="38t13iivtqfad">
                    Web conferencing details provided upon confirmation.
                  </span>
                </div>
              </div>
              <p className="mt-8 text-sm text-neutral-500 leading-relaxed" data-id="element-6173" data-mp="2z03poq1s1ei3">
                We'll show you how Giga can automate your specific workflows and
                answer any questions you have about security, integration, and
                pricing.
              </p>
            </div>

            {/* Right calendar panel */}
            <div className="p-8 md:p-10 md:w-2/3 flex flex-col md:flex-row gap-8" data-id="element-6174" data-mp="2l3gfvmoq6tur">
              <div className="flex-1" data-id="element-6175" data-mp="2g32kb44b9cdm">
                <h2 className="font-bold text-lg text-white mb-6" data-id="element-6176" data-mp="vki9zkm1b94n">
                  Select a Date & Time
                </h2>

                <div className="flex items-center justify-between mb-6" data-id="element-6177" data-mp="10ia14ceuur7s">
                  <span className="text-white font-medium" data-id="element-6178" data-mp="14l6ffqxd415x">June 2026</span>
                  <div className="flex gap-2" data-id="element-6179" data-mp="14j3q7941ac29">
                    <button className="p-2 rounded-lg hover:bg-white/5 text-neutral-400 transition-colors" data-id="element-6180" data-mp="19iyfx9c2ovlq">
                      <ChevronLeft className="w-5 h-5" data-id="element-6181" data-mp="cv1w7vlc9k6a" />
                    </button>
                    <button className="p-2 rounded-lg hover:bg-white/5 text-neutral-400 transition-colors" data-id="element-6182" data-mp="gukjuz4jo9ct">
                      <ChevronRight className="w-5 h-5" data-id="element-6183" data-mp="ltdu8iojbk8r" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-2 mb-2" data-id="element-6184" data-mp="qmgjiema47j5">
                  {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => <div key={day} className="text-center text-xs font-bold text-neutral-500 py-2" data-id="element-6185" data-mp="3ua8mz5bmbo94">
                        {day}
                      </div>)}
                </div>

                <div className="grid grid-cols-7 gap-2" data-id="element-6186" data-mp="20pf7lbgueju5">
                  {/* Empty slots for offset */}
                  <div className="aspect-square" data-id="element-6187" data-mp="1vqlrrce32gqt"></div>

                  {Array.from({
                length: 30
              }).map((_, i) => {
                const date = i + 1;
                const isPast = date < 15;
                const isSelected = selectedDate === date;
                return <button key={date} disabled={isPast} onClick={() => {
                  setSelectedDate(date);
                  setSelectedTime(null);
                }} className={`
                          aspect-square rounded-full flex items-center justify-center text-sm font-medium transition-all
                          ${isPast ? 'text-neutral-600 cursor-not-allowed' : isSelected ? 'bg-[#EA580C] text-white shadow-lg shadow-[#EA580C]/20' : 'text-neutral-300 hover:bg-white/10 hover:text-white'}
                        `} data-id="element-6188" data-mp="2jk3velcgqdm3" data-mpi={i}>
                        {date}
                      </button>;
              })}
                </div>

                <div className="mt-8 flex items-center gap-2 text-sm text-neutral-500" data-id="element-6189" data-mp="o19ab6px8b0p">
                  <Globe className="w-4 h-4" data-id="element-6190" data-mp="a9kxt2brfkj7" />
                  <span data-id="element-6191" data-mp="a8teapvftd9a">Pacific Time - US & Canada</span>
                </div>
              </div>

              {/* Time slots (shows when date is selected) */}
              {selectedDate && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="md:w-48 flex flex-col gap-3" data-id="element-6192" data-mp="a3vmuvpvorz">
                  <h3 className="font-medium text-white mb-3" data-id="element-6193" data-mp="3ngwh8arod6nv">
                    {new Date(2026, 5, selectedDate).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
                  </h3>
                  <div className="flex flex-col gap-2 h-[300px] overflow-y-auto pr-2 custom-scrollbar" data-id="element-6194" data-mp="1qo4dbncs9mhb">
                    {timeSlots.map(time => <div key={time} className="flex gap-2" data-id="element-6195" data-mp="1o9phtxtyyxah">
                        <button onClick={() => setSelectedTime(time)} className={`
                            flex-1 py-3 rounded-xl text-sm font-medium transition-all border
                            ${selectedTime === time ? 'bg-neutral-700 border-neutral-600 text-white' : 'bg-transparent border-white/10 text-[#EA580C] hover:border-[#EA580C]'}
                          `} data-id="element-6196" data-mp="1k6t70tao85h3">
                          {time}
                        </button>
                        {selectedTime === time && <button onClick={handleBook} className="bg-[#EA580C] text-white px-4 rounded-xl text-sm font-bold hover:bg-[#C2410C] transition-colors shadow-lg shadow-[#EA580C]/20 whitespace-nowrap" data-id="element-6197" data-mp="2306jniainp7w">
                            Next
                          </button>}
                      </div>)}
                  </div>
                </motion.div>}
            </div>
          </motion.div> : <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-[#2B211A] border border-white/10 rounded-3xl shadow-2xl p-12 text-center max-w-2xl mx-auto" data-id="element-6198" data-mp="2lulhv5p07duo">
            <div className="w-20 h-20 bg-[#EA580C]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-6199" data-mp="335r3rf0cf57h">
              <div className="w-14 h-14 bg-[#EA580C] rounded-full flex items-center justify-center shadow-lg shadow-[#EA580C]/30" data-id="element-6200" data-mp="3864yjvcxoink">
                <Check className="w-7 h-7 text-white" data-id="element-6201" data-mp="3c4m9yr3e0fas" />
              </div>
            </div>
            <h2 className="font-title font-bold text-3xl text-white mb-4" data-id="element-6202" data-mp="3h3ywtbu9dmdd">
              You are scheduled
            </h2>
            <p className="text-neutral-400 text-lg max-w-md mx-auto mb-8" data-id="element-6203" data-mp="3lyur6imywltn">
              A calendar invitation has been sent to your email address. We look
              forward to showing you Giga in action.
            </p>
            <button onClick={() => window.location.href = '/'} className="text-neutral-400 hover:text-white font-medium transition-colors" data-id="element-6204" data-mp="3qdpk6b0qutp">
              Return to home
            </button>
          </motion.div>}
      </div>
    </div>;
}